import { useForm, type UseFormReturn } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject, useToast } from '@kanda-libs/ks-design-library';
import {
  actions,
  JobInfo,
  PreferenceMap,
} from '@kanda-libs/ks-frontend-services';
import { URLS } from 'config';
import { useApiError, useAppDispatch } from 'hooks';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { useEnquiryContext } from '../context';
import { formatEnquiryFormValuesToEnquiryBody } from './helpers';

export interface EnquiryWorkDetailsFormValues {
  job_info: JobInfo;
  consumer_preferences: PreferenceMap;
}

export interface EnquiryWorkDetailsFormHook {
  form: UseFormReturn<EnquiryWorkDetailsFormValues>;
  onSubmit: (
    formValues: StringIndexedObject<EnquiryWorkDetailsFormValues>,
  ) => void;
  tradingName: string;
  isLoading: boolean;
  isSubmitting: boolean;
}

export default function useEnquiryWorkDetailsForm(): EnquiryWorkDetailsFormHook {
  const { id, enquiry, tradingName, isLoading, isSubmitting, setIsSubmitting } =
    useEnquiryContext();
  const dispatch = useAppDispatch();
  const onApiError = useApiError('Unknown error submitting enquiry');
  const { push } = useHistory();
  const { showSuccess } = useToast();

  const defaultValues = useMemo(() => {
    if (!enquiry) return undefined;
    const { enquiry_info: enquiryInfo } = enquiry;
    if (!enquiryInfo) return undefined;
    return {
      ...enquiryInfo,
    };
  }, [enquiry]);

  const form = useForm<EnquiryWorkDetailsFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues,
  });

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<EnquiryWorkDetailsFormValues>) => {
      if (!id) return;
      const { jobInfo, consumerPreferences } =
        formatEnquiryFormValuesToEnquiryBody(formValues);
      setIsSubmitting(true);
      dispatch(
        actions.postEnquiryJobInfo({
          body: jobInfo,
          params: { id },
          onError: (err) => {
            setIsSubmitting(false);
            onApiError(err);
          },
          onSuccess: () => {
            dispatch(
              actions.postEnquiryConsumerPreferences({
                body: consumerPreferences,
                params: { id },
                onError: (err) => {
                  setIsSubmitting(false);
                  onApiError(err);
                },
                onSuccess: () => {
                  setIsSubmitting(false);
                  showSuccess('Initial enquiry posted');
                  push(generatePath(URLS.enquiryProjectBudget, { id }));
                },
              }),
            );
          },
        }),
      );
    },
    [dispatch, id, onApiError, push, showSuccess, setIsSubmitting],
  );

  const { reset } = form;

  const initRef = useRef<boolean>(false);
  useEffect(() => {
    if (!enquiry || !defaultValues || initRef.current) return;
    initRef.current = true;
    reset(defaultValues);
  }, [enquiry, defaultValues, reset]);

  return {
    form,
    onSubmit,
    tradingName,
    isLoading,
    isSubmitting,
  };
}
